import React, { useState } from 'react';
import { Grid, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { API_BASE_URL } from 'config/api';
import dayjs from 'dayjs';
import MemberCard from '../memberCard';

// Import styled components
import {
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionTitleText,
  ViewAllButton,
} from './styled';

const RecentMembersSectionView = ({ 
  members = [],
  activeMembers = [], 
  onMemberClick, 
  onViewAll,
  isAdmin = false
}) => {
  // State for menu handling (same as MembersTab)
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Helper function for time formatting (same as MembersTab)
  const getTimeAgo = (timestamp) => {
    const now = dayjs();
    const time = dayjs(timestamp);
    const diffHours = now.diff(time, 'hour');

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = now.diff(time, 'day');
    if (diffDays < 7) return `${diffDays}d ago`;

    return time.format('MMM D');
  };

  // Menu handlers (same as MembersTab)
  const handleMenuOpen = (event, member) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  // Menu actions
  const handleMessageMember = () => {
    if (onMemberClick && selectedMember) {
      onMemberClick(selectedMember, 'message');
    }
    handleMenuClose();
  };

  const handleViewProfile = () => {
    if (onMemberClick && selectedMember) {
      onMemberClick(selectedMember, 'profile');
    }
    handleMenuClose();
  };

  const handleChangeRole = () => {
    if (onMemberClick && selectedMember) {
      onMemberClick(selectedMember, 'role');
    }
    handleMenuClose();
  };

  const handleRemoveMember = () => {
    if (onMemberClick && selectedMember) {
      onMemberClick(selectedMember, 'remove');
    }
    handleMenuClose();
  };

  // Review handler
  const handleReviewMember = (member) => {
    if (onMemberClick) {
      onMemberClick(member, 'review');
    }
  };

  // Process real member data to match expected format
  const processedMembers = members.map(member => ({
    id: member.id,
    slug: member.slug,
    name: member.user ? 
      `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim() || 'Unknown Member' :
      member.name || 'Unknown Member',
    firstName: member.user?.first_name || '',
    lastName: member.user?.last_name || '',
    email: member.user?.email || '',
    bio: member.user?.description || member.bio || '',
    location: member.user?.location || member.location || '',
    avatar: member.user?.avatar || member.avatar || '',
    joined: member.create_date || dayjs().subtract(Math.floor(Math.random() * 30), 'day').format(),
    joinedDate: member.create_date || dayjs().subtract(Math.floor(Math.random() * 30), 'day').format(),
    lastActivity: member.create_date ? 
      dayjs(member.create_date).add(Math.floor(Math.random() * 48), 'hour').valueOf() :
      dayjs().subtract(Math.floor(Math.random() * 48), 'hour').valueOf(),
    isOnline: member.id ? (member.id % 3 === 0) : Math.random() > 0.5, // Simple online simulation
    isVerified: member.user?.is_verified || member.isVerified || false,
    role: member.role || 'PA',
    status: member.status || 'AC'
  }));

  // Filter to get only active members and sort by most recent
  const activeMembersData = processedMembers
    .filter(member => member.status === 'AC') // Only active members
    .sort((a, b) => dayjs(b.joined).valueOf() - dayjs(a.joined).valueOf()) // Sort by join date
    .slice(0, 4); // Show only 4 most recent
  const membersToShow = activeMembersData.length > 0 ? activeMembersData : [];

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>
          <PeopleIcon />
          <SectionTitleText>
            Recent Members ({membersToShow.length})
          </SectionTitleText>
        </SectionTitle>
        
        <ViewAllButton onClick={onViewAll}>
          View All
        </ViewAllButton>
      </SectionHeader>
      
      {membersToShow.length > 0 ? (
        <Grid container spacing={3}>
          {membersToShow.map((member) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
              <MemberCard
                member={member}
                isAdmin={isAdmin}
                onMemberClick={onMemberClick}
                onMenuOpen={handleMenuOpen}
                onReviewMember={handleReviewMember}
                getTimeAgo={getTimeAgo}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          color: '#8D6E63'
        }}>
          <PeopleIcon style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
          <p>No recent members to display</p>
        </div>
      )}

      {/* Member Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewProfile}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Profile</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleMessageMember}>
          <ListItemIcon>
            <MessageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Message</ListItemText>
        </MenuItem>

        {isAdmin && selectedMember && (
          <>
            <MenuItem onClick={handleChangeRole}>
              <ListItemIcon>
                <AdminPanelSettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Change Role</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={handleRemoveMember} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <PersonRemoveIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>Remove from Community</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </SectionContainer>
  );
};

export default RecentMembersSectionView;